<template>
  <div>
    <div class="page-header">
      <div class="page-icon">📦</div>
      <h2>批量入货</h2>
    </div>
    <div class="content-card">
    <!-- 扫描条形码输入框 -->
    <el-input
      ref="barcodeInput"
      v-model="barcode"
      placeholder="扫描条形码"
      @keyup.enter="scanProduct"
    ></el-input>

    <!-- 手动输入商品名称 -->
    <el-input
      v-model="manualInput"
      placeholder="或者手动输入商品名称"
      @keyup.enter="searchProductByInput"
      clearable
    >
      <template #append>
        <el-button
          icon="el-icon-search"
          @click="searchProductByInput"
        ></el-button>
      </template>
    </el-input>

    <!-- 商品列表 -->
    <el-card class="box-card" shadow="hover" style="margin-top: 20px">
      <div style="margin-bottom: 10px;">
        <el-button 
          :type="sortOrder === 'asc' ? 'primary' : 'default'"
          @click="toggleSortOrder"
        >
          {{ sortOrder === 'asc' ? '正序' : '倒序' }}
        </el-button>
      </div>

      <el-table ref="productTableRef" :data="sortedProducts" style="width: 100%" height="calc(100vh - 360px)">
        <el-table-column label="商品名称" prop="product_name"></el-table-column>
        <el-table-column label="条形码" prop="barcode"></el-table-column>
        <el-table-column label="商品类别" prop="category"></el-table-column>
        <el-table-column label="数量">
          <template #default="{ row }">
            <el-input
              v-model.number="row.quantity"
              type="number"
              min="1"
              @change="updateQuantity(row)"
              style="width: 80px"
            />
          </template>
        </el-table-column>
        <el-table-column label="单价">
          <template #default="{ row }">
            <el-input
              v-model.number="row.selling_price"
              placeholder="请输入单价"
              type="number"
              min="0.01"
              style="width: 100px"
            />
          </template>
        </el-table-column>
        <el-table-column label="操作">
          <template #default="{ row }">
            <el-button type="danger" @click="removeProduct(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 总价和总数量 -->
    <div class="totals">
      <el-row>
        <el-col :span="12">
          <strong>总数量：</strong>{{ totalQuantity }}
        </el-col>
        <el-col :span="12" class="text-right">
          <strong>总价格：</strong>{{ totalPrice.toFixed(1) }} 元
        </el-col>
      </el-row>
    </div>

    <!-- 按钮 -->
    <el-button @click="clearScannedList">清空</el-button>
    <el-button type="primary" @click="processCheckout">结算</el-button>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, nextTick } from "vue";
import { ElMessage, ElMessageBox } from "element-plus";
import axios from "axios";
import API_BASE from '@/api';
import dayjs from "dayjs";

export default {
  setup() {
    const barcode = ref(""); // 扫码输入
    const manualInput = ref(""); // 手动输入商品名称
    const scannedProducts = ref([]); // 已扫描商品列表
    const barcodeInput = ref(null); // 获取条形码输入框的 ref
    const sortOrder = ref('asc'); // 排序状态，默认正序（添加顺序）
    const productTableRef = ref(null); // 表格 ref，用于自动滚动

    // 表格内部自动滚到最后一行（让用户能立即看到新加的商品）
    const scrollToLastRow = async () => {
      await nextTick();
      // 用 scrollIntoView 直接定位最后一行，更可靠
      const wrapper = productTableRef.value?.$el?.querySelector('.el-table__body-wrapper');
      if (!wrapper) return;
      // 等待 el-table 内部渲染完成（最多 250ms）
      let prev = -1;
      for (let i = 0; i < 10; i++) {
        await new Promise(r => requestAnimationFrame(r));
        if (wrapper.scrollHeight === prev) break;
        prev = wrapper.scrollHeight;
      }
      // 同时设 scrollTop 兜底（某些 el-table 版本 scrollHeight 计算滞后）
      wrapper.scrollTop = wrapper.scrollHeight;
      // 再用 scrollIntoView 找最后一行滚到位
      const rows = wrapper.querySelectorAll('tbody tr');
      const lastRow = rows[rows.length - 1];
      if (lastRow) {
        lastRow.scrollIntoView({ block: 'end', inline: 'nearest', behavior: 'auto' });
      }
    };

    // 计算总数量
    const totalQuantity = computed(() =>
      scannedProducts.value.reduce((sum, product) => sum + Number(product.quantity), 0)
    );

    // 计算总价格
    const totalPrice = computed(() =>
      scannedProducts.value.reduce(
        (sum, product) => sum + product.selling_price * product.quantity,
        0
      )
    );

    // 按添加顺序排序的商品列表
    const sortedProducts = computed(() => {
      const products = [...scannedProducts.value]; // 创建副本
      return sortOrder.value === 'asc' ? products : products.reverse();
    });

    // 扫描条形码
    const scanProduct = async () => {
      if (!barcode.value) {
        ElMessage.error("请输入有效的条形码");
        return;
      }
      try {
        const response = await axios.get(
          `${API_BASE}/api/product/${barcode.value}`
        );
        if (response.data.success) {
          const product = response.data.data;
          const existingProduct = scannedProducts.value.find(
            (item) => item.barcode === product.barcode
          );
          if (existingProduct) {
            existingProduct.quantity += 1;
          } else {
            scannedProducts.value.push({
              product_id: product.product_id,
              product_name: product.product_name,
              barcode: product.barcode,
              category: product.category,
              quantity: 1,
              selling_price: parseFloat(product.selling_price) || 0,
            });
          }
          await scrollToLastRow();
        } else {
          ElMessage.error("商品未找到");
        }
      } catch (error) {
        ElMessage.error("扫码失败");
        console.error(error);
      } finally {
        barcode.value = "";
      }
    };

    // 手动输入商品名称查询
    const searchProductByInput = async () => {
      if (!manualInput.value) {
        ElMessage.error("请输入有效的商品名称");
        return;
      }
      try {
        const response = await axios.get(
          `${API_BASE}/api/product/name/${encodeURIComponent(
            manualInput.value
          )}`
        );
        if (response.data.success) {
          const product = response.data.data;
          product.forEach((item) => {
            const existingProduct = scannedProducts.value.find(
              (scanned) => scanned.barcode === item.barcode
            );
            if (existingProduct) {
              existingProduct.quantity += 1;
            } else {
              scannedProducts.value.push({
                product_id: item.product_id,
                product_name: item.product_name,
                barcode: item.barcode,
                category: item.category,
                quantity: 1,
                selling_price: parseFloat(item.selling_price) || 0,
              });
            }
          });
          await scrollToLastRow();
        } else {
          ElMessage.error("商品未找到");
        }
      } catch (error) {
        ElMessage.error("查询失败");
        console.error(error);
      } finally {
        manualInput.value = "";
      }
    };

    // 更新数量
    const updateQuantity = (product) => {
      if (product.quantity < 1) {
        product.quantity = 1; // 保证数量至少为1
      }
    };

    // 切换排序顺序
    const toggleSortOrder = () => {
      sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
    };

    // 清空扫描列表
    const clearScannedList = () => {
      scannedProducts.value = [];
    };

    // 删除单个商品
    const removeProduct = (product) => {
      const index = scannedProducts.value.findIndex(
        (item) => item.barcode === product.barcode
      );
      if (index !== -1) {
        scannedProducts.value.splice(index, 1);
      }
    };

    // 结算
    const processCheckout = async () => {
      try {
        await ElMessageBox.confirm(
          `总数量：${totalQuantity.value}，总价格：${totalPrice.value.toFixed(
            2
          )} 元，确认入货结算吗？`,
          "确认结算",
          {
            confirmButtonText: "确定",
            cancelButtonText: "取消",
            type: "warning",
          }
        );
        const response = await axios.post(`${API_BASE}/api/purchases/batch`, {
          products: scannedProducts.value.map((product) => ({
            product_id: product.product_id,
            transaction_price: product.selling_price,
            transaction_quantity: product.quantity,
            transaction_date: dayjs().format("YYYY-MM-DD HH:mm:ss"),
          })),
        });
        if (response.data.success) {
          ElMessage.success("结算成功");
          clearScannedList();
        } else {
          ElMessage.error("结算失败：" + response.data.message);
        }
      } catch (error) {
        ElMessage.error("结算出错");
        console.error(error);
      }
    };

    onMounted(() => {
      barcodeInput.value.focus();
    });

    return {
      barcode,
      manualInput,
      scannedProducts,
      sortOrder,
      sortedProducts,
      scanProduct,
      searchProductByInput,
      clearScannedList,
      processCheckout,
      totalQuantity,
      totalPrice,
      barcodeInput,
      updateQuantity,
      removeProduct,
      toggleSortOrder,
      productTableRef,
      scrollToLastRow,
    };
  },
};
</script>

<style scoped>
.cashier-desk {
  padding: 0;
}

.el-card {
  margin-top: 0;
  border-radius: var(--radius-md);
  padding: 20px;
  background-color: #fff;
  border: 1px solid var(--border-lighter);
  box-shadow: var(--shadow-card);
}

.el-card .el-table {
  margin-top: 10px;
}

.totals {
  margin-top: 20px;
  font-size: 16px;
  font-weight: 600;
  padding: 14px 18px;
  background: var(--primary-lighter);
  border-radius: var(--radius-sm);
  color: var(--text-primary);
  border: 1px solid #c2dcff;
}

.totals strong {
  color: var(--primary);
}

.el-button {
  margin-top: 10px;
  margin-right: 8px;
}

.text-right {
  text-align: right;
}

/* 表格内部出现独立滚动条（让新加的行能自动滚到视口内） */
:deep(.el-table) {
  display: flex;
  flex-direction: column;
}

:deep(.el-table__inner-wrapper) {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

:deep(.el-table__body-wrapper) {
  flex: 1 1 auto !important;
  position: relative !important;
  overflow-y: auto !important;
  min-height: 0;
  scroll-behavior: smooth;
}
</style>
